import React, { useState, useEffect, useMemo, useCallback } from "react";
// import { Link } from "react-router-dom"; // Ensure Link is imported if used elsewhere
import {
    Box,
    Typography,
    TextField,
    Button,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    Paper,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    Card,
    CardContent,
    TableBody,
    Grid,
    TableSortLabel,
    Alert,
    Tooltip,
    InputLabel,
    Autocomplete,
    Chip,
    Stack, // Added for filter layout potentially
} from "@mui/material";
// Import necessary icons
import { Delete, Edit, Save, Cancel, FilterList as FilterListIcon, Clear, UploadFile } from "@mui/icons-material";
import config from './config.js';
import api from './api';
import { isTokenExpired } from './auth';

// Define roles
const ROLES = { 1: "Admin", 2: "Moderator" };

// --- Sorting Helper Functions ---
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1]; // Fallback to original index for stability
  });
  return stabilizedThis.map((el) => el[0]);
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// *** FIX: Robust descendingComparator ***
function descendingComparator(a, b, orderBy) {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Handle potential null/undefined values robustly
    const aIsNull = aValue === null || typeof aValue === 'undefined';
    const bIsNull = bValue === null || typeof bValue === 'undefined';

    if (aIsNull && bIsNull) return 0; // Both null/undefined, treat as equal
    if (aIsNull) return 1; // Place nulls after non-nulls in ascending sort (so -1 * 1 = -1 for desc)
    if (bIsNull) return -1; // Place nulls after non-nulls in ascending sort (so -1 * -1 = 1 for desc)

    // Actual comparison for non-null values
    if (typeof aValue === 'number' && typeof bValue === 'number') {
        // Numeric comparison
        if (bValue < aValue) return -1;
        if (bValue > aValue) return 1;
        return 0;
    } else {
        // String comparison (case-insensitive)
        // Convert to string *after* null check
        const strA = String(aValue).toLowerCase();
        const strB = String(bValue).toLowerCase();
        // localeCompare handles strings nicely
        // Adjust comparison based on order ('desc' means b comes before a if b > a)
        if (strB < strA) return -1;
        if (strB > strA) return 1;
        return 0;
    }
}
// --- End Sorting Helpers ---


function TeamManagement() {
    // --- State ---
    const [teams, setTeams] = useState([]); // State holding fetched teams
    const [allUsers, setAllUsers] = useState([]);
    const [cohorts, setCohorts] = useState([]); // Full cohort list for dropdowns [{cohort_id, cohort_name}]
    const [distinctCohorts, setDistinctCohorts] = useState([]); // Unique names for filter dropdown

    const [newTeamName, setNewTeamName] = useState("");
    const [newTeamCohortId, setNewTeamCohortId] = useState("");

    const [editingTeam, setEditingTeam] = useState(null); // Team object being edited
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null); // For general errors
    const [uploadStatus, setUploadStatus] = useState({ message: '', error: false }); // For CSV upload feedback
    const [selectedFile, setSelectedFile] = useState(null);

    // Sorting state
    const [sortColumn, setSortColumn] = useState('team_name');
    const [sortOrder, setSortOrder] = useState('asc');

    // Filter state
    const [cohortFilter, setCohortFilter] = useState(''); // Stores selected cohort_name for filtering
    const [adminFilterId, setAdminFilterId] = useState(''); // Stores selected user_id for filtering

    // Assignment state (for the team being edited)
    const [assignedAdminsLoading, setAssignedAdminsLoading] = useState(false);
    const [assignedAdminsError, setAssignedAdminsError] = useState(null); // Error specific to assignment fetching/saving
    const [assignedAdmins, setAssignedAdmins] = useState([]); // Stores original admins assigned to the team being edited
    const [selectedAssignedAdmins, setSelectedAssignedAdmins] = useState([]); // Stores current selection in Autocomplete
    const [availableAdmins, setAvailableAdmins] = useState([]); // Admins/Mods list for Autocomplete options & filter

    const token = localStorage.getItem("token");

    // --- Data Fetching ---
    const fetchData = useCallback(async () => {
         if (!token || isTokenExpired()) {
             localStorage.removeItem('token'); localStorage.removeItem('userRole'); localStorage.removeItem('userName'); localStorage.removeItem('userId');
             setError("Session expired. Please log in."); setLoading(false); return;
         }
        setLoading(true); setError(null); let isMounted = true;
        try {
            const [teamResponse, userResponse, cohortResponse] = await Promise.all([
                api.get(`${config.backendUrl}/teams`),
                api.get(`${config.backendUrl}/users`),
                api.get(`${config.backendUrl}/cohorts`)
            ]);

            const teamsData = Array.isArray(teamResponse.data) ? teamResponse.data : [];
            console.log("Fetched Teams Data:", teamsData);
            const usersData = Array.isArray(userResponse.data) ? userResponse.data : [];
            console.log("Fetched Users Data:", usersData);
            const cohortsData = Array.isArray(cohortResponse.data) ? cohortResponse.data : [];
            console.log("Fetched Cohorts Data:", cohortsData);

            // Expecting cohortsData to be [{cohort_id, cohort_name}]
            const mappedCohorts = cohortsData
                .filter(c => c && c.cohort_id != null && c.cohort_name != null)
                .map(c => ({ cohort_id: c.cohort_id, cohort_name: c.cohort_name }));

            // Extract distinct cohort names from fetched teams for filtering
            const distinctCohortNames = [...new Set(teamsData.map(team => team.cohort_name).filter(Boolean))];

            // Filter users to get available Admins/Moderators for dropdowns/autocomplete
            const adminUsers = usersData.filter(user => user.role_id === 1 || user.role_id === 2);
            console.log("Filtered Available Admins/Mods:", adminUsers);

            if (isMounted) {
                setTeams(teamsData); // Includes assigned_admins list and assigned_admin_names string
                setAllUsers(usersData);
                setAvailableAdmins(adminUsers);
                setCohorts(mappedCohorts); // Use mapped cohorts
                setDistinctCohorts(distinctCohortNames); // Unique names for filter dropdown
            }
        } catch (err) {
            console.error("Data fetching error:", err);
            let errorMsg = "An unknown error occurred during data fetching.";
             if (err.response) { errorMsg = `Error fetching data: ${err.response.status} ${err.response.data?.message || err.response.statusText}`; }
             else if (err.request) { errorMsg = "Network error. Could not reach the server."; }
             else { errorMsg = err.message || errorMsg; }
            if (isMounted) setError(errorMsg);
        } finally {
            if (isMounted) setLoading(false);
        }
        return () => { isMounted = false; }; // Cleanup
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // --- Fetch Assigned Admins when Editing Team Starts ---
    const fetchAssignedAdmins = useCallback(async (teamId) => {
        setAssignedAdminsLoading(true); setAssignedAdminsError(null); setAssignedAdmins([]); setSelectedAssignedAdmins([]);
        try {
            const response = await api.get(`${config.backendUrl}/teams/${teamId}/admins`);
            console.log(`Fetched assigned admins for team ${teamId}:`, response.data);
            const fetchedAdmins = response.data || [];
            setAssignedAdmins(fetchedAdmins); // Store the original list fetched
            setSelectedAssignedAdmins(fetchedAdmins); // Initialize the selection state
        } catch (err) {
             console.error(`Error fetching assigned admins for team ${teamId}:`, err);
             let errorMsg = "Could not load assigned admins/mods.";
             if (err.response) { errorMsg = `Error loading assigned admins: ${err.response.status} ${err.response.data?.message || err.response.statusText}`; }
             else if (err.request) { errorMsg = "Network error fetching assigned admins."; }
             else { errorMsg = err.message || errorMsg; }
            setAssignedAdminsError(errorMsg);
        } finally { setAssignedAdminsLoading(false); }
    }, []); // Empty dependency array is correct here

    // --- Memoized Filtering and Sorting ---
    const processedTeams = useMemo(() => {
        console.log("Processing teams. Cohort Filter:", cohortFilter, "Admin Filter:", adminFilterId);
        let filtered = teams.filter(team => {
            const cohortMatch = !cohortFilter || team.cohort_name === cohortFilter;
            let adminMatch = true;
            if (adminFilterId) {
                const assignedAdminIds = Array.isArray(team.assigned_admins) ? team.assigned_admins.map(admin => admin.user_id) : [];
                adminMatch = assignedAdminIds.includes(Number(adminFilterId));
            }
            return cohortMatch && adminMatch;
        });
        console.log("Filtered teams count:", filtered.length);
        return stableSort(filtered, getComparator(sortOrder, sortColumn));
    }, [teams, cohortFilter, adminFilterId, sortColumn, sortOrder]);

    // --- Event Handlers ---
    const handleSort = (columnId) => {
        const isAsc = sortColumn === columnId && sortOrder === 'asc';
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortColumn(columnId);
    };

    const handleCreateTeam = async () => {
        if (!newTeamName.trim()) { setError("Team name cannot be empty."); return; }
        setError(null);
        try {
            const payload = {
                team_name: newTeamName,
                cohort_id: newTeamCohortId === "" ? null : parseInt(newTeamCohortId, 10)
            };
            const response = await api.post(`${config.backendUrl}/teams`, payload);
            const createdTeam = response.data; // Expecting {team_id, team_name, cohort_id}
            // Manually add cohort_name if cohort_id exists
            if (createdTeam.cohort_id) {
                const cohort = cohorts.find(c => c.cohort_id === createdTeam.cohort_id);
                createdTeam.cohort_name = cohort ? cohort.cohort_name : null;
            } else {
                 createdTeam.cohort_name = null;
            }
            createdTeam.assigned_admins = []; // Initialize as empty
            createdTeam.assigned_admin_names = ''; // Initialize as empty

            setTeams(prevTeams => [...prevTeams, createdTeam]);
            setNewTeamName(""); setNewTeamCohortId("");
            // Update distinct cohorts if a new one was implicitly used
            const updatedCohorts = [...new Set([...teams, createdTeam].map(team => team.cohort_name).filter(Boolean))];
            setDistinctCohorts(updatedCohorts);
        } catch (err) { console.error("Error creating team:", err); setError(err.response?.data?.message || err.message || "Failed to create team."); }
    };

    const handleEdit = (team) => {
        setEditingTeam({ ...team, cohort_id: team.cohort_id ?? "" }); // Ensure cohort_id is string or empty string for Select
        fetchAssignedAdmins(team.team_id);
    };

    const handleCancelEdit = () => {
        setEditingTeam(null);
        setAssignedAdmins([]);
        setSelectedAssignedAdmins([]);
        setAssignedAdminsError(null);
        setError(null);
    };

    const handleAssignedAdminsChange = (event, newValue) => {
        console.log("Autocomplete onChange newValue:", newValue);
        setSelectedAssignedAdmins(newValue);
    };

    const handleUpdateTeam = async (teamId) => {
        console.log(`TeamManagement: handleUpdateTeam called for teamId: ${teamId}`, "Current editingTeam state:", editingTeam);
        if (!editingTeam || editingTeam.team_id !== teamId || !editingTeam.team_name?.trim()) {
            setError("Invalid team data for update."); return;
        }
        setError(null); setAssignedAdminsError(null);

        let updatedTeamData = null;
        let assignmentsProcessed = false;

        try {
            // --- 1. Update Core Team Details ---
            const corePayload = {
                team_id: editingTeam.team_id,
                team_name: editingTeam.team_name,
                cohort_id: editingTeam.cohort_id === "" ? null : parseInt(editingTeam.cohort_id, 10)
            };
            console.log("TeamManagement: Attempting to update core team details with payload:", corePayload);
            const response = await api.put(`${config.backendUrl}/teams/${teamId}`, corePayload);
            updatedTeamData = response.data; // Backend returns updated team object {team_id, team_name, cohort_id, cohort_name}
            console.log("TeamManagement: Core team update successful:", updatedTeamData);

            // --- 2. Update Admin Assignments ---
            console.log("TeamManagement: Processing admin assignments...");
            const originalAssignedAdminIds = new Set(assignedAdmins.map(admin => admin.user_id));
            const newSelectedAdminIds = new Set(selectedAssignedAdmins.map(admin => admin.user_id));
            const adminsToAssign = [...newSelectedAdminIds].filter(id => !originalAssignedAdminIds.has(id));
            const adminsToUnassign = [...originalAssignedAdminIds].filter(id => !newSelectedAdminIds.has(id));
            console.log("TeamManagement: Admins to Assign:", adminsToAssign);
            console.log("TeamManagement: Admins to Unassign:", adminsToUnassign);
            const assignmentPromises = [];

            if (adminsToAssign.length > 0) {
                 console.log(`TeamManagement:  -> Scheduling POST /admins/${adminsToAssign.join(',')}/teams with team_id: ${teamId}`); // Log batch assignment
                 // Assuming backend can handle multiple assignments for a single user in one call if needed,
                 // otherwise loop as before. For simplicity, let's assume one call per user is needed.
                 adminsToAssign.forEach(adminId => {
                     assignmentPromises.push( api.post(`${config.backendUrl}/admins/${adminId}/teams`, { team_ids: [teamId] }) );
                 });
            }
            adminsToUnassign.forEach(adminId => {
                console.log(`TeamManagement:  -> Scheduling DELETE /admins/${adminId}/teams/${teamId}`);
                assignmentPromises.push( api.delete(`${config.backendUrl}/admins/${adminId}/teams/${teamId}`) );
            });

            if (assignmentPromises.length > 0) {
                const results = await Promise.allSettled(assignmentPromises);
                console.log("TeamManagement: Assignment API call results:", results);
                const assignmentErrors = results .filter(result => result.status === 'rejected') .map(result => result.reason?.response?.data?.message || result.reason?.message || 'Unknown assignment error');
                if (assignmentErrors.length > 0) {
                    setAssignedAdminsError(`Failed to update some admin assignments: ${assignmentErrors.join(', ')}`);
                    fetchAssignedAdmins(teamId); // Re-fetch on partial failure
                } else { setAssignedAdminsError(null); }
            } else { console.log("TeamManagement: No admin assignment changes detected."); setAssignedAdminsError(null); }
            assignmentsProcessed = true;

            // --- 3. Re-fetch Data and Finish Edit Mode ---
            console.log(`TeamManagement: Update process complete for team ${teamId}. Re-fetching data...`);
            await fetchData(); // Re-fetch the entire team list for consistency
            handleCancelEdit(); // Exit edit mode only after successful fetch

        } catch (err) {
            console.error(`TeamManagement: Error during update for team ${teamId}:`, err);
            let errorMsg = "An error occurred while updating team.";
            if (err.response) { errorMsg = `Error updating team: ${err.response.status} ${err.response.data?.message || err.response.statusText}`; }
            else if (err.request) { errorMsg = "Network error updating team."; }
            else { errorMsg = err.message || errorMsg; }
            if (!updatedTeamData) { setError(errorMsg); }
            else if (!assignmentsProcessed) { setAssignedAdminsError(errorMsg); fetchAssignedAdmins(teamId); }
            else { setError(errorMsg); }
            // Do NOT reset edit state on error
        }
    };

    const handleDeleteTeam = async (teamId) => {
        const members = allUsers.filter(user => user.team_id === teamId);
        const confirmationMessage = members.length > 0 ? `This team has ${members.length} member(s). Deleting the team will unassign them. Are you sure?` : "Are you sure you want to delete this team?";
        if (!window.confirm(confirmationMessage)) return; setError(null);
        try {
            await api.delete(`${config.backendUrl}/teams/${teamId}`);
            // Re-fetch data to ensure consistency after delete
            await fetchData();
            // setTeams(prevTeams => prevTeams.filter(team => team.team_id !== teamId)); // Let fetchData handle update
            // const updatedTeams = teams.filter(team => team.team_id !== teamId);
            // const distinctCohortNames = [...new Set(updatedTeams.map(team => team.cohort_name).filter(Boolean))];
            // setDistinctCohorts(distinctCohortNames);
        } catch (err) { console.error("Error deleting team:", err); setError(err.response?.data?.message || err.message || "Failed to delete team."); }
    };

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
        setUploadStatus({ message: '', error: false });
    };

    const handleUpload = async () => {
        if (!selectedFile) { setUploadStatus({ message: "Please select a CSV file.", error: true }); return; }
        setUploadStatus({ message: 'Uploading...', error: false }); setError(null);
        const formData = new FormData(); formData.append("file", selectedFile);
        try {
            const response = await api.post(`${config.backendUrl}/upload_teams_csv`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            setUploadStatus({ message: response.data.message || "CSV uploaded successfully! Refreshing data...", error: false });
            setSelectedFile(null); document.getElementById('team-csv-upload').value = '';
            await fetchData(); // Refresh data after upload
        } catch (err) { console.error("Error uploading CSV:", err); const errorMsg = err.response?.data?.message || err.message || "Failed to upload CSV."; setUploadStatus({ message: errorMsg, error: true }); setError(errorMsg); }
    };

    // --- Filter Handlers ---
    const handleCohortFilterChange = (event) => { setCohortFilter(event.target.value); };
    const handleAdminFilterChange = (event) => { setAdminFilterId(event.target.value); };
    const clearFilters = () => { setCohortFilter(''); setAdminFilterId(''); };

    // --- Helper Functions ---
    const getTeamMembers = (teamId) => { const members = allUsers.filter(user => user.team_id === teamId); return members.length > 0 ? `${members.length} Member(s)` : "0 Members"; };

     // --- Render Logic ---
    const filteredTeamsForDropdown = useMemo(() => {
        if (!cohortFilter) return teams.sort((a, b) => (a.team_name ?? '').localeCompare(b.team_name ?? ''));
        return teams.filter(team => team.cohort_name === cohortFilter).sort((a, b) => (a.team_name ?? '').localeCompare(b.team_name ?? ''));
    }, [teams, cohortFilter]);

    if (loading) { return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}><CircularProgress /></Box>; }
    // Show general error, but still allow table rendering if teams exist
    // if (error && !teams.length) { return <Box sx={{ p: 3, textAlign: 'center' }}><Alert severity="error" sx={{ mb: 2 }}>{error}</Alert><Button variant="contained" onClick={() => window.location.reload()}>Retry</Button></Box>; }

    const hasTeamsToDisplay = processedTeams.length > 0;
    const headCells = [ { id: 'team_name', label: 'Team Name', numeric: false }, { id: 'cohort_name', label: 'Cohort Name', numeric: false }, { id: 'team_members', label: 'Team Members', numeric: false, sortable: false }, { id: 'assigned_admins', label: 'Assigned Admins/Mods', numeric: false, sortable: false }, { id: 'actions', label: 'Actions', numeric: false, sortable: false, align: 'center' } ];


    return (
        <Box sx={{ p: 2, maxWidth: '1200px', margin: 'auto' }}>
            <Typography variant="h4" gutterBottom component="h1">Team Management</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {/* --- Create Team Section --- */}
             <Card variant="outlined" sx={{ mb: 3 }}> <CardContent> <Typography variant="h6" gutterBottom>Create New Team</Typography> <Grid container spacing={2} alignItems="flex-end"> <Grid item xs={12} sm={6}> <TextField label="New Team Name" value={newTeamName} onChange={(e) => setNewTeamName(e.target.value)} fullWidth variant="outlined" size="small" /> </Grid> <Grid item xs={12} sm={4}> <FormControl fullWidth size="small"> <InputLabel id="create-team-cohort-label">Assign Cohort (Optional)</InputLabel> <Select labelId="create-team-cohort-label" value={newTeamCohortId} label="Assign Cohort (Optional)" onChange={(e) => setNewTeamCohortId(e.target.value)} > <MenuItem value=""><em>None</em></MenuItem> {cohorts.sort((a, b) => a.cohort_name.localeCompare(b.cohort_name)).map((cohort) => ( <MenuItem key={cohort.cohort_id} value={cohort.cohort_id}> {cohort.cohort_name} </MenuItem> ))} </Select> </FormControl> </Grid> <Grid item xs={12} sm={2}> <Button variant="contained" color="primary" onClick={handleCreateTeam} fullWidth> Create </Button> </Grid> </Grid> </CardContent> </Card>
            {/* --- CSV Upload Section --- */}
            <Card variant="outlined" sx={{ mb: 3 }}> <CardContent> <Typography variant="h6" gutterBottom>Upload Teams via CSV</Typography> <Grid container spacing={2} alignItems="center"> <Grid item xs={12} sm={8}> <TextField type="file" id="team-csv-upload" onChange={handleFileChange} size="small" fullWidth inputProps={{ accept: ".csv" }} helperText={selectedFile ? `Selected: ${selectedFile.name}` : "Select CSV (cohort_name, team_name, group_key, user_email, admin_email)"} /> </Grid> <Grid item xs={12} sm={4}> <Button variant="contained" color="secondary" onClick={handleUpload} disabled={!selectedFile} startIcon={<UploadFile />} fullWidth > Upload CSV </Button> </Grid> </Grid> {uploadStatus.message && ( <Alert severity={uploadStatus.error ? "error" : "success"} sx={{ mt: 2 }}> {uploadStatus.message} </Alert> )} </CardContent> </Card>
            {/* --- Filter Controls --- */}
             <Paper sx={{ p: 2, mb: 2 }} variant="outlined"> <Grid container spacing={2} alignItems="center"> <Grid item> <FilterListIcon /> </Grid> <Grid item> <Typography variant="h6">Filter Teams</Typography> </Grid> <Grid item xs={12} sm={6} md={4}> <FormControl fullWidth size="small"> <InputLabel id="cohort-filter-label">By Cohort</InputLabel> <Select labelId="cohort-filter-label" value={cohortFilter} label="By Cohort" onChange={handleCohortFilterChange} > <MenuItem value=""><em>All Cohorts</em></MenuItem> {distinctCohorts.sort().map((cohortName) => ( <MenuItem key={cohortName} value={cohortName}> {cohortName} </MenuItem> ))} </Select> </FormControl> </Grid> <Grid item xs={12} sm={6} md={4}> <FormControl fullWidth size="small"> <InputLabel id="admin-filter-label">By Assigned Admin/Mod</InputLabel> <Select labelId="admin-filter-label" value={adminFilterId} label="By Assigned Admin/Mod" onChange={handleAdminFilterChange} > <MenuItem value=""><em>Any Admin/Mod</em></MenuItem> {availableAdmins.sort((a,b) => (a.name ?? '').localeCompare(b.name ?? '')).map((admin) => ( <MenuItem key={admin.user_id} value={admin.user_id}> {admin.name} ({admin.email}) </MenuItem> ))} </Select> </FormControl> </Grid> <Grid item xs={12} sm={12} md="auto"> <Button onClick={clearFilters} size="medium" startIcon={<Clear />} variant="outlined" color="secondary" sx={{ height: '40px' }} disabled={!cohortFilter && !adminFilterId} > Clear Filters </Button> </Grid> </Grid> </Paper>
            {/* --- Team Table --- */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Team Details</Typography>
            <TableContainer component={Paper}>
                <Table stickyHeader aria-label="team management table">
                    <TableHead> <TableRow> {headCells.map((headCell) => ( <TableCell key={headCell.id} align={headCell.align || (headCell.numeric ? 'right' : 'left')} sortDirection={sortColumn === headCell.id ? sortOrder : false} > {headCell.sortable !== false ? ( <TableSortLabel active={sortColumn === headCell.id} direction={sortColumn === headCell.id ? sortOrder : 'asc'} onClick={() => handleSort(headCell.id)} > {headCell.label} </TableSortLabel> ) : ( headCell.label )} </TableCell> ))} </TableRow> </TableHead>
                    <TableBody>
                        {hasTeamsToDisplay ? (
                            processedTeams.map((team) => (
                                <React.Fragment key={team.team_id}>
                                    {/* Display/Edit Row */}
                                    <TableRow hover sx={ editingTeam?.team_id === team.team_id ? { backgroundColor: 'action.hover' } : {} } >
                                         <TableCell> {editingTeam?.team_id === team.team_id ? ( <TextField value={editingTeam.team_name} onChange={(e) => setEditingTeam({ ...editingTeam, team_name: e.target.value })} fullWidth size="small" variant="outlined" /> ) : ( team.team_name )} </TableCell>
                                         <TableCell> {editingTeam?.team_id === team.team_id ? ( <FormControl fullWidth size="small"> <Select value={editingTeam.cohort_id ?? ""} onChange={(e) => setEditingTeam({ ...editingTeam, cohort_id: e.target.value })} displayEmpty > <MenuItem value=""><em>None</em></MenuItem> {cohorts.sort((a, b) => a.cohort_name.localeCompare(b.cohort_name)).map((cohort) => ( <MenuItem key={cohort.cohort_id} value={cohort.cohort_id}> {cohort.cohort_name} </MenuItem> ))} </Select> </FormControl> ) : ( team.cohort_name || "N/A" )} </TableCell>
                                         <TableCell>{getTeamMembers(team.team_id)}</TableCell>
                                         <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}> <Tooltip title={team.assigned_admin_names || ""}> <span>{team.assigned_admin_names || "None"}</span> </Tooltip> </TableCell>
                                         <TableCell align="center"> <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'nowrap' }}> {editingTeam?.team_id === team.team_id ? ( <> <Tooltip title="Save Changes"> <Button variant="contained" size="small" color="primary" onClick={() => handleUpdateTeam(team.team_id)} startIcon={<Save />} sx={{ minWidth: 'auto', px: 1 }} disabled={!editingTeam.team_name?.trim()}/> </Tooltip> <Tooltip title="Cancel Edit"> <Button variant="outlined" size="small" color="secondary" onClick={handleCancelEdit} startIcon={<Cancel />} sx={{ minWidth: 'auto', px: 1 }}/> </Tooltip> </> ) : ( <> <Tooltip title="Edit Team"> <Button variant="outlined" size="small" color="primary" startIcon={<Edit />} onClick={() => handleEdit(team)} sx={{ minWidth: 'auto', px: 1 }}/> </Tooltip> <Tooltip title="Delete Team"> <Button variant="outlined" size="small" color="error" startIcon={<Delete />} onClick={() => handleDeleteTeam(team.team_id)} sx={{ minWidth: 'auto', px: 1 }}/> </Tooltip> </> )} </Box> </TableCell>
                                    </TableRow>
                                    {/* Row for Editing Admin Assignments */}
                                    {editingTeam?.team_id === team.team_id && (
                                        <TableRow>
                                            <TableCell colSpan={headCells.length} sx={{ p: 2, borderBottom: 'none' }}>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Manage Assigned Admins/Moderators:</Typography>
                                                {assignedAdminsLoading && <CircularProgress size={20} />}
                                                {assignedAdminsError && <Alert severity="error" size="small" sx={{ mb: 1 }}>{assignedAdminsError}</Alert>}
                                                {!assignedAdminsLoading && (
                                                    <Autocomplete
                                                        multiple id={`assigned-admins-autocomplete-${team.team_id}`}
                                                        options={availableAdmins}
                                                        getOptionLabel={(option) => `${option.name} (${option.email})` || ""}
                                                        value={selectedAssignedAdmins}
                                                        onChange={handleAssignedAdminsChange}
                                                        isOptionEqualToValue={(option, value) => option.user_id === value.user_id}
                                                        renderTags={(value, getTagProps) => value.map((option, index) => ( <Chip variant="outlined" label={option.name} {...getTagProps({ index })} /> ))}
                                                        renderInput={(params) => ( <TextField {...params} variant="outlined" label="Assigned Admins/Mods" placeholder="Select users..." size="small" /> )}
                                                        sx={{ mt: 1 }}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))
                        ) : (
                             <TableRow> <TableCell colSpan={headCells.length} align="center"> {cohortFilter || adminFilterId ? "No teams match the current filters." : "No teams found."} </TableCell> </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

// Accessibility helper (define if not already globally available)
const visuallyHidden = { border: 0, clip: 'rect(0 0 0 0)', height: 1, margin: -1, overflow: 'hidden', padding: 0, position: 'absolute', top: 20, width: 1, };

export default TeamManagement;
